import type { PaletteKey, PixelCanvas } from '../../lib';

/**
 * Weapons share one construction: a hand-drawn *fill* grid (no outline, 1-px margin) wrapped in a
 * "Storybook" selective outline whose colour follows the material it touches — a darker tone of that
 * material, lighter on the lit top/left side — so every gun speaks the same outline language.
 */
type Material = 'brass' | 'orange' | 'wood' | 'skin' | 'steel' | 'green' | 'red' | 'blue' | 'purple';

const MATERIAL: Record<PaletteKey, Material> = {
  k: 'steel',
  '1': 'steel',
  '2': 'steel',
  '3': 'steel',
  '4': 'steel',
  '5': 'steel',
  w: 'steel',
  N: 'wood',
  n: 'wood',
  m: 'wood',
  t: 'wood',
  e: 'wood',
  S: 'skin',
  s: 'skin',
  a: 'skin',
  R: 'red',
  r: 'red',
  x: 'red',
  i: 'red',
  u: 'orange',
  o: 'orange',
  U: 'brass',
  Y: 'brass',
  y: 'brass',
  D: 'green',
  G: 'green',
  g: 'green',
  l: 'green',
  B: 'blue',
  b: 'blue',
  c: 'blue',
  P: 'purple',
  p: 'purple',
};

/**
 * Outline colour per material, by which edge of the part it sits on. Every tone stays well below the ground's value
 * (grass g, lane m) so a gun lying on the floor keeps its rim; rust `u` / skin `S` rims matched the grass in greyscale.
 */
const SELOUT: Record<Material, readonly [litTopLeft: PaletteKey, shadeRight: PaletteKey, bottom: PaletteKey]> = {
  brass: ['n', 'N', 'N'],
  orange: ['n', 'N', 'N'],
  wood: ['n', 'N', 'N'],
  skin: ['n', 'N', 'N'],
  steel: ['2', '1', '1'],
  green: ['G', 'D', 'D'],
  red: ['R', 'N', 'N'],
  blue: ['B', '1', '1'],
  purple: ['P', 'N', 'N'],
};

/** 4-neighbour outline whose colour follows the material it wraps (see SELOUT). */
export function gunOutline(c: PixelCanvas): PixelCanvas {
  const add: [number, number, PaletteKey][] = [];
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      if (c.get(x, y) !== null) continue;
      const above = c.get(x, y - 1);
      const left = c.get(x - 1, y);
      const right = c.get(x + 1, y);
      const below = c.get(x, y + 1);
      if (above !== null) add.push([x, y, SELOUT[MATERIAL[above]][2]]);
      else if (left !== null) add.push([x, y, SELOUT[MATERIAL[left]][1]]);
      else if (right !== null) add.push([x, y, SELOUT[MATERIAL[right]][0]]);
      else if (below !== null) add.push([x, y, SELOUT[MATERIAL[below]][0]]);
    }
  }
  for (const [x, y, col] of add) c.set(x, y, col);
  return c;
}

/**
 * Held, the grip (`hand`) sits at art row 55 of the 72-row gardener (AIM_HEIGHT, `player-view.ts`), under his big head,
 * so what is behind the grip meets his face: nothing there in skin or hair tones, a stock no more than 6 art px over the
 * origin row (the chin clears it aimed sideways) and steel (aimed down it swings across the face; a tan walnut stock
 * read as a moustache). The fist on the grip is the gardener's own, drawn by the client in their skin (ADR-0073).
 */

/**
 * Frame source for a 4×-density gun: its fill grid, then the sel-out twice, so the rim stays 2 art px: half a world px,
 * the 2× guns' weight on screen.
 */
export const gun4 = (fill: string) => (c: PixelCanvas) => {
  c.stamp(fill);
  gunOutline(gunOutline(c));
};
